import { getIn, useFormikContext } from 'formik';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

import { invariant } from '@e-socna/utils';
import { EMPTY_OBJECT } from '@e-socna/empty';

import { getTouchedError } from '../../../utils/get-touched-error.util';
import { MissionCategoryFormField } from '../../../components/MissionCategoryAutocomplete/MissionCategoryFormField';
import { LazyEditor } from '../../../components/Editor/Lazy';
import type { FileStorage } from '../../../components/Editor/types/file-storage.type';
import type { DocumentResolversSettings } from '../../../components/Editor/types/document-resolvers-settings.type';

import type { ResolvableMissionTypeForm_ResolvableMissionType } from '../fragments/resolvable-mission-type.fragment';

interface Props {
  readonly id: string;
  readonly resolversSettings: DocumentResolversSettings;
  readonly storage: FileStorage;
}

export const ResolvableMissionTypeInfoFormStep = ({
  resolversSettings,
  storage,
}: Readonly<Props>) => {
  const {
    errors,
    handleBlur,
    handleChange,
    initialValues,
    isSubmitting,
    touched,
    values,
  } = useFormikContext<ResolvableMissionTypeForm_ResolvableMissionType>();

  invariant('code' in values.codingConfig, 'Unexpected codingConfig type');

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          md: 4,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'codingConfig.code') &&
              getIn(touched, 'codingConfig.code')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'codingConfig.code')}
          id="resolvable-mission-type-coding-config-code"
          label="Code"
          name="codingConfig.code"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={values.codingConfig.code}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          md: 8,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={!!(errors.label && touched.label)}
          fullWidth
          helperText={getTouchedError(errors, touched, 'label')}
          id="resolvable-mission-type-label"
          label="Libellé"
          name="label"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={values.label}
          variant="outlined"
        />
      </Grid>
      <MissionCategoryFormField required />
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <TextField
          error={!!(errors.title && touched.title)}
          fullWidth
          helperText={getTouchedError(errors, touched, 'title')}
          id="resolvable-mission-type-title"
          label="Titre"
          name="title"
          onBlur={handleBlur}
          onChange={handleChange}
          type="text"
          value={values.title}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={12}
        sx={{ display: 'flex', flexDirection: 'column', pt: 1, pb: 1 }}
      >
        <LazyEditor
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(errors.content
            ? {
                alert: {
                  severity: 'error',
                  content: errors.content,
                },
              }
            : EMPTY_OBJECT)}
          disabled={isSubmitting}
          error={!!(errors.content && touched.content)}
          id={values._id}
          initialValue={initialValues.content}
          label="Contenu"
          name="content"
          onBlur={handleBlur}
          onChange={handleChange}
          required={false}
          resolversSettings={resolversSettings}
          storage={storage}
          value={values.content}
        />
      </Grid>
    </Grid>
  );
};
